import type {
  RequestRoute,
  Tool,
  ToolAccessSource,
  ToolAvailability,
} from "@/hooks/types";

/**
 * One place for the availability vocabulary and its presentation.
 *
 * Availability is written in exactly one place (ADR 0012): a
 * `tool_configs.status`, one BU's own availability for a tool. The group does
 * not make tools available across the board — it signs contracts naming the
 * units they cover (`tools.coveredBusinessUnitIds`), and coverage says who may
 * adopt, not how available something is. Nor does the group *block*: a business unit
 * that refuses a tool refuses it in its own config and binds nobody else.
 *
 * The option list and the pill colours belong here rather than being restated
 * per page, so the admin pages cannot drift apart.
 */

/**
 * Authoring order for availability selects. Mirrors the backend
 * `ToolAvailability` enum and the import's availability vocabulary
 * (`import_common._AVAILABILITY_BY_VALUE`); the user-facing mapping of sheet
 * words onto these values is documented in the `import` i18n bundles'
 * `approvalStatusNote`.
 *
 * Derived from a `satisfies`-checked record rather than written out as an array,
 * for the reason `TOOL_ACCESS_SOURCES` below is: an array annotated
 * `readonly ToolAvailability[]` type-checks while *missing* a member. That is
 * not a hypothetical here — every test in `availability.test.ts` that partitions
 * this list, including the two that exist to stop a new availability joining the
 * predicates quietly, is only as total as the array. An eighth value added to
 * the union and to everything `tsc` forces, but not here, leaves them all
 * green.
 *
 * The key order is the authoring order, and `Object.keys` preserves it.
 */
const AVAILABILITY_KEYS = {
  READY: true,
  QUICK: true,
  STANDARD: true,
  UNDER_REVIEW: true,
  BLOCKED: true,
  PREINSTALLED: true,
  PASSIVE: true,
} satisfies Record<ToolAvailability, true>;

export const TOOL_AVAILABILITIES = Object.keys(
  AVAILABILITY_KEYS,
) as readonly ToolAvailability[];

/**
 * What an admin may pick when *authoring* a tool (the Add-tool dialog and the
 * import's availability column) — everything except UNDER_REVIEW, which
 * describes a tool already in an evaluation, not a disposition someone chooses
 * on creation. Editing an existing BU config offers the full list, so the two
 * differ on purpose.
 */
export const AUTHORABLE_AVAILABILITIES: readonly ToolAvailability[] =
  TOOL_AVAILABILITIES.filter((a) => a !== "UNDER_REVIEW");

/**
 * Every grant source the register can label, mirroring the backend
 * `ToolAccessSource`. Exported as a runtime list, not only a type, because a
 * value arriving from Firestore has to be *checked* against it — a stored
 * source outside this list must fall back to "source not recorded" rather
 * than be cast into a lookup that has no entry for it.
 *
 * Derived from a `satisfies`-checked record rather than written out as an
 * array, so the list cannot quietly become a *subset* of the union: an array
 * annotated `readonly ToolAccessSource[]` type-checks while missing a member,
 * and a missing member here is a real source silently reading as unrecorded.
 */
const ACCESS_SOURCE_KEYS = {
  REQUEST: true,
  ADMIN: true,
  SELF_DECLARED: true,
  PREINSTALLED: true,
  TAKEN: true,
} satisfies Record<ToolAccessSource, true>;

export const TOOL_ACCESS_SOURCES = Object.keys(
  ACCESS_SOURCE_KEYS,
) as readonly ToolAccessSource[];

/**
 * Availability pill colours, light and dark.
 *
 * One table for every surface. There were two — this one for the admin lists,
 * and a second written inline in `ToolStatusBadge` for the member-facing
 * catalogue — and they disagreed about two of the seven words: a blocked tool
 * was grey to an admin and red to a member, on the one value the register
 * cannot afford to soften (ADR 0006). The member-facing palette won, because
 * that is the one people learn.
 */
export const AVAILABILITY_PILL: Record<ToolAvailability, string> = {
  READY:
    "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
  QUICK:
    "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800",
  STANDARD:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  UNDER_REVIEW:
    "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800",
  BLOCKED:
    "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
  PREINSTALLED:
    "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",
  PASSIVE:
    "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800",
};

/**
 * The same seven colours as a single dot, for the one place the vocabulary
 * appears as plain text: the availability select (Jira NAV-105).
 *
 * A dropdown of seven words that everywhere else in the app are colours is a
 * list you have to read rather than scan, and the reader has already learned
 * the colours from the badges. A dot rather than a pill because the select's
 * own row is the label; two boxes in one row would be a badge inside a badge.
 *
 * A second table rather than something derived from `AVAILABILITY_PILL`,
 * because Tailwind builds the class list from literal strings in the source —
 * a computed `bg-${colour}-500` is a class that never gets generated. So they
 * are two literals that must agree, and `availability.test.ts` reads the colour
 * family out of each and fails if they ever say different things.
 *
 * **The badge is what these match, not `docs/design_guidelines.md`'s status
 * palette**, and that is worth stating because the two differ. The guidelines
 * give BLOCKED a grey (`#6b7280`, the `status.blocked` token); the badges give
 * it red, and that departure is the decision recorded above — a refusal has to
 * read as a refusal (ADR 0006), and the member-facing palette won because it is
 * the one people learn. A dot taken from the guidelines would sit in a
 * different colour from the badge beside it, which is the exact defect
 * `AVAILABILITY_PILL` was consolidated to end. The `status.*` tokens are still
 * live and still the guidelines' — they label a *user account's* state on the
 * users pages, which is a different vocabulary that happens to share a word.
 */
export const AVAILABILITY_DOT: Record<ToolAvailability, string> = {
  READY: "bg-green-500",
  QUICK: "bg-blue-500",
  STANDARD: "bg-amber-500",
  UNDER_REVIEW: "bg-purple-500",
  BLOCKED: "bg-red-500",
  PREINSTALLED: "bg-sky-500",
  PASSIVE: "bg-teal-500",
};

/**
 * The frame a surface wears when the whole of it is about one availability —
 * a panel rather than a pill (Jira NAV-90).
 *
 * Only the refusal has one. Every other availability differs from its
 * neighbours in *degree* — how long it takes, who agrees first — which the pill
 * inside the frame already says; a refusal differs in *kind*, because it is the
 * answer that ends the reader's errand, and a neutral box made it the easiest
 * state on the page to skim past. Here beside the pills so the frame and the
 * badge it holds stay the same red, and so the next surface that has to say
 * "refused" does not invent a second one.
 */
export const REFUSAL_PANEL =
  "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20";

/**
 * The vocabulary's own words: one **name** and one **one-line meaning** per
 * availability, in the `common` bundle so every surface says the same thing
 * (Jira NAV-72).
 *
 * Tyll asked that a reader be able to see what each availability means rather
 * than infer it from a turnaround like "1-2 days"; the testers in the same week
 * said the copy already on those screens was too long and said the same thing
 * twice. So the answer is one short line per word, shown wherever the badge is,
 * and not a second paragraph — a page that wants to explain an availability
 * reaches for these keys instead of writing a sentence of its own.
 *
 * Keyed by the stored value rather than by a hand-written camelCase alias, so a
 * word added to `ToolAvailability` cannot be given a label on one screen and
 * left without one on the next: the record is total, and `availability.test.ts`
 * asserts both keys exist in **both** bundles.
 */
const AVAILABILITY_COPY: Record<ToolAvailability, string> = {
  READY: "common:availability.READY",
  QUICK: "common:availability.QUICK",
  STANDARD: "common:availability.STANDARD",
  UNDER_REVIEW: "common:availability.UNDER_REVIEW",
  BLOCKED: "common:availability.BLOCKED",
  PREINSTALLED: "common:availability.PREINSTALLED",
  PASSIVE: "common:availability.PASSIVE",
};

/** What this availability is called, wherever it is named. */
export function availabilityLabelKey(availability: ToolAvailability): string {
  return `${AVAILABILITY_COPY[availability]}.label`;
}

/** What it means for whoever is reading it, in one line. */
export function availabilityMeaningKey(availability: ToolAvailability): string {
  return `${AVAILABILITY_COPY[availability]}.meaning`;
}

/** The subset of `Tool` the coverage predicate reads. */
type Coverage = Pick<Tool, "isGlobal" | "coveredBusinessUnitIds">;

/**
 * Does a group contract cover this business unit (ADR 0012)? There is no
 * "globally approved" state — what exists is contracts naming the units they
 * cover. False for a BU-scoped entry — a BU's own catalogue entry is held,
 * not covered.
 *
 * This is the *only* thing the group says about a tool. Whether a covered
 * business unit then makes it available, or refuses to, is that business unit's own config.
 */
export function coversBu(
  tool: Coverage,
  businessUnitId: string | null | undefined,
): boolean {
  if (tool.isGlobal === false || !businessUnitId) return false;
  return (tool.coveredBusinessUnitIds ?? []).includes(businessUnitId);
}

/**
 * The availability an entry falls back to when nobody has said anything at
 * all — neither the business unit on its config nor the group on the entry.
 *
 * STANDARD, which is what an unconfigured tool has always routed as: a request
 * happens and a person decides it. Named rather than written twice so the
 * group's default and a statusless config cannot drift onto different answers.
 */
const FALLBACK_AVAILABILITY: ToolAvailability = "STANDARD";

/** A stored string read as an availability, or `null` when it is not one.
 *
 *  Both fields this module reads — `tools.defaultAvailability` and
 *  `tool_configs.status` — arrive from Firestore through interfaces that are
 *  claims rather than checks, so either can hold a word this vocabulary has
 *  never had. Handing one of those to `AVAILABILITY_PILL` or the route table
 *  yields `undefined`, which renders as an unstyled pill and reads as *no
 *  route* — a tool nobody could ask for, on the strength of a typo. */
export function asAvailability(value: unknown): ToolAvailability | null {
  return typeof value === "string" &&
    (TOOL_AVAILABILITIES as readonly string[]).includes(value)
    ? (value as ToolAvailability)
    : null;
}

/**
 * The **group's default** for a catalogue entry (Jira NAV-147, ADR 0024).
 *
 * What a member is shown, and what a request follows, in a business unit that has not
 * written its own config for the tool. A default and never an override — a
 * business unit's own availability wins outright, and one business unit's refusal binds
 * nobody else, which is what keeps this on the right side of the line ADR 0012
 * drew: the group makes no *disposition*, it states a starting point.
 *
 * Absent, or a word outside the vocabulary, reads as STANDARD — the value an
 * unconfigured entry has always routed as, so no catalogue needs migrating and
 * a malformed one degrades to the route with a person in it.
 */
export function groupDefaultAvailability(
  tool: { defaultAvailability?: ToolAvailability | null } | null | undefined,
): ToolAvailability {
  return asAvailability(tool?.defaultAvailability) ?? FALLBACK_AVAILABILITY;
}

/**
 * What a stored config says, for the two readers that hold one and not the
 * tool it belongs to.
 *
 * A config that exists but names no availability is a business unit that adopted the
 * tool and said nothing else. That reads as the same fallback an entry with no
 * group default gets, which is the only honest answer available without the
 * entry in hand — and the reason this is separate from `availabilityFor`
 * rather than the same function with an optional argument: a caller that
 * *can* see the entry must not quietly settle for the weaker answer.
 */
export function configAvailability(
  status: ToolAvailability | null | undefined,
): ToolAvailability {
  return asAvailability(status) ?? FALLBACK_AVAILABILITY;
}

/**
 * **The availability, full stop**: what this business unit's members meet for this
 * tool, what its page offers, and what a request for it follows.
 *
 * The business unit's own word when it has written one; the group's default otherwise.
 * There is no third answer and no absent one — which is the whole of Jira
 * NAV-147. Before it, a business unit that had said nothing produced `null`, and the
 * app had to carry that null everywhere and word it: the pill read "Not yet
 * evaluated" while the page below it offered a standard request, so the same
 * tool said two things, and "nobody has looked at this" was printed on tools
 * the group had already contracted for.
 *
 * One function rather than the expression spelled out per surface, for the
 * reason the two-answer version was a bug in the first place: written out
 * twice, the catalogue list and the tool's own page drift apart, and nothing in
 * either file says which was meant.
 */
export function availabilityFor(
  tool: { defaultAvailability?: ToolAvailability | null } | null | undefined,
  config: { status?: ToolAvailability | null } | null | undefined,
): ToolAvailability {
  const own = asAvailability(config?.status);
  if (own) return own;
  // A config that exists and names nothing is not a decision, so it falls
  // through to the group's default exactly as no config at all does. The
  // *existence* of the config still means the business unit has settled the entry, and
  // that is deliberately a different question from this one — asked by the one
  // surface that cares, the unit's tool-management page, by testing the config
  // for null. Not worth a predicate of its own: it had one, left with no caller
  // once the queue tab derived the split from the config list it already holds,
  // and a named wrapper round `!= null` that nothing calls is a second
  // definition waiting to disagree with the first.
  return groupDefaultAvailability(tool);
}

/**
 * Does the business unit's position on this tool amount to an offer to its members?
 *
 * The question a category's drill-down splits on: "available in your business
 * unit" above, everything else below. A `Record` over the vocabulary rather
 * than two lists of values, and that is the whole point of moving it here — the
 * page had one hand-written list per group, and an availability missing from
 * both would have silently vanished from the category it belongs to (Jira
 * NAV-122). A word added to `ToolAvailability` now fails to compile until
 * somebody says which side of this line it falls on.
 *
 * PASSIVE counts as offered: it grants nothing to manage, but a member may use
 * the tool passively, which is a permission and not a refusal (ADR 0017,
 * amended for Jira NAV-71). UNDER_REVIEW does not — the tool is in an
 * evaluation and cannot be asked for at all (Jira NAV-147) — and neither,
 * obviously, does BLOCKED.
 *
 * **Total, with no null case.** There used to be one, for a business unit that had
 * written no config, and it is gone with the state itself: every entry now
 * carries an availability, the business unit's own or the group's default (NAV-147).
 * A business unit that has not decided is therefore grouped by what its members are
 * actually offered, which is what they see either way — and whether the *business unit*
 * has settled the entry is a different question, which only the unit's own
 * tool-management page asks.
 *
 * Telling the two non-offers apart is the badge's job, not this predicate's:
 * what a member is owed before deciding whether to ask is which of "refused"
 * and "being evaluated" they are reading, and grouping cannot say two things.
 * `availabilityFor` is what the badge is given, in the lists this predicate
 * groups and on the tool's own page alike, so the two surfaces cannot word the
 * same tool differently.
 */
const BUSINESS_UNIT_OFFERS: Record<ToolAvailability, boolean> = {
  READY: true,
  QUICK: true,
  STANDARD: true,
  PREINSTALLED: true,
  PASSIVE: true,
  UNDER_REVIEW: false,
  BLOCKED: false,
};

export function businessUnitOffers(availability: ToolAvailability): boolean {
  return BUSINESS_UNIT_OFFERS[availability];
}

/**
 * Which route a request for this tool takes, or `null` when it raises no
 * request at all (ADR 0017 decisions 1-3).
 *
 * A total function over the availability vocabulary, so a value added to
 * `ToolAvailability` fails to compile until somebody decides what it means here.
 * That is the point: three of these words meant nothing to the application until
 * Tyll gave each one a route, and the way to keep the next one from drifting
 * back into decoration is to make the omission a type error.
 *
 * `null` covers three unrelated reasons for having no route, which the caller
 * has to tell apart itself — READY is taken in one action, BLOCKED is refused,
 * and PASSIVE is a statement about the tool rather than an offer to anybody.
 * This function answers "does a request happen, and where does it go", not
 * "what should the page offer".
 *
 * **Mirrored in two other places, deliberately and unavoidably.**
 * `firestore.rules` re-states it because the rules are the boundary — the screen
 * not offering a form is a courtesy, and a hand-crafted write that claimed the
 * quick route for a standard tool would otherwise skip the approver entirely
 * (NAV-65). The backend does *not* re-state it: a trigger reads the route the
 * rules pinned on the request, so there is no third copy to drift.
 * `availability.test.ts` asserts this table row by row, and the rules tests
 * assert the refusals, so a change here that is not made there fails.
 */
const REQUEST_ROUTE_BY_AVAILABILITY: Record<
  ToolAvailability,
  RequestRoute | null
> = {
  // Everybody in the unit already has it — there is nothing to ask for.
  READY: null,
  // Refused by the business unit. No route, and no form.
  BLOCKED: null,
  // Routed, although the page offers no request for it. Every active member
  // already holds a pre-installed tool, so nobody asks — except somebody whose
  // automatic grant an admin revoked on purpose, whose page offers the ordinary
  // request path as the only way back (ADR 0015 decision 5). Giving it a route
  // is what keeps that door open: neither this table nor the rules can see the
  // revocation, so refusing the availability would shut the door on exactly the
  // people who need it.
  PREINSTALLED: "APPROVAL",
  // Nothing to buy, so nobody approves: straight to whoever sets it up.
  QUICK: "ACTIVATION",
  // The cost is a real question, so an approver settles it first.
  STANDARD: "APPROVAL",
  // Informational, and therefore no route at all (Jira NAV-71): passive
  // availability says what may be done with a tool nobody has to be given —
  // joining a call rather than hosting one — and Tyll settled that people
  // should not be managing it. It used to route like STANDARD, which sent a
  // request to an approver with nothing to decide. Nearer to BLOCKED than to a
  // route, in the shape of the answer and in nothing else: one is a refusal,
  // the other is a permission that needs no request.
  PASSIVE: null,
  // No route, because a tool in an evaluation is not something a colleague can
  // ask for (Jira NAV-147). It used to route like STANDARD — the safest of the
  // three, on the reasoning that a person should decide rather than the app
  // hand off silently — which meant a request could be raised against a tool
  // whose evaluation had not finished, and then decided on nothing. It becomes
  // requestable when the evaluation concludes and the business unit records an
  // availability, which is the moment the answer exists.
  //
  // The third kind of `null`, and it is neither of the other two: this is not
  // a refusal (BLOCKED) and not a permission that needs no request (PASSIVE),
  // it is "not yet". `isUnderEvaluation` is what lets a page say so.
  UNDER_REVIEW: null,
};

export function requestRouteFor(
  availability: ToolAvailability,
): RequestRoute | null {
  return REQUEST_ROUTE_BY_AVAILABILITY[availability];
}

/**
 * What the **group** may set as an entry's default (Jira NAV-147, ADR 0024):
 * **the words that raise a request**, and no others.
 *
 * One positive rule rather than a list of exclusions, and the difference is not
 * stylistic. Written as a subtraction this set silently admitted every word
 * nobody had thought to remove — which is how READY sat in it, handing every
 * member of every unsettled business unit a working take button (see below). Derived
 * from the route table, a word added to `ToolAvailability` is admitted only if
 * it earns a route.
 *
 * **Why "raises a request" is the right line.** A default is what a business unit's
 * members meet *before that business unit has decided anything*. A request is the one
 * outcome that does not settle the question on the business unit's behalf: it goes to
 * that business unit's own people, who decide it, and the entry is meanwhile sitting in
 * their queue with `AVAILABILITY_NEEDED` pointing at it. Every word without a
 * route ends the member's errand instead — and does it on the group's say-so,
 * with the business unit never told, because the notice rides on a request document
 * that is never created. That covers all five:
 *
 * - **READY** grants access. A member takes the tool in one action and a
 *   `tool_access` record lands, in a business unit that never offered it — and the
 *   rules gate that take on this very function, so the group's word is what
 *   authorises it. PRD principle 1 allows self-granting as an exception resting
 *   on "the admin setting that availability **for the unit**"; under a group
 *   default no admin of that unit set anything, so the exception's own
 *   reasoning is gone and access is granted with no human decision in the business unit
 *   that bears it.
 * - **PREINSTALLED** is the same harm through the other door: it is a claim
 *   about a *machine image*, which belongs to a business unit, and the fan-out that
 *   makes it true watches `tool_configs` and nothing else (ADR 0015). It has a
 *   route only for the member whose automatic grant was revoked, which is why
 *   the derivation below has to exclude it by name — the one word that earns a
 *   route without being a thing the group may say.
 * - **BLOCKED** is a refusal, and a refusal is a business unit's own act (ADR 0012,
 *   PRD principle 13). NAV-147 asks for the opposite in as many words: a
 *   request on an unsettled entry reaches the business unit's admin "rather than being
 *   refused".
 * - **PASSIVE** is not a refusal — it is a permission — but the chain it breaks
 *   is identical, and that is what matters here: no route, so no request, so no
 *   notice, so the business unit is never told. A member who needs a real seat (hosting
 *   a call rather than joining one) meets a dead end with no alternative
 *   offered and nobody to chase.
 * - **UNDER_REVIEW** describes a tool inside an evaluation — a business unit's own
 *   circumstance, and unaskable everywhere at once if the group set it.
 *
 * A business unit can still set any of the seven on its own config, where each means
 * what it says and where the grant, the refusal and the evaluation are its own.
 */
export const GROUP_DEFAULT_AVAILABILITIES: readonly ToolAvailability[] =
  TOOL_AVAILABILITIES.filter(
    (a) => requestRouteFor(a) !== null && a !== "PREINSTALLED",
  );

/**
 * Is a member's own one-click "add this to my tools" the way they get this tool?
 *
 * `READY` alone, and asked narrowly on purpose: it reads like "has no route",
 * which `BLOCKED` and `PASSIVE` also satisfy, and a take button on a tool the
 * business unit refused — or on one that was never anybody's to hold — would record
 * access nobody offered.
 */
export function isTakeable(availability: ToolAvailability): boolean {
  return availability === "READY";
}

/**
 * Is this the business unit's **refusal** — the one availability that stops a member
 * rather than routing them somewhere?
 *
 * Asked narrowly, and not folded into `offersNoAction`: that predicate is true
 * of PASSIVE as well, and passive availability is a permission nobody has to
 * ask for. Telling a member the two apart is the whole job of a surface that
 * has to look different for one of them (Jira NAV-90) — a refusal that reads
 * like an ordinary state is the one message this register cannot afford to
 * soften (ADR 0006).
 */
export function isRefusal(availability: ToolAvailability): boolean {
  return availability === "BLOCKED";
}

/**
 * Is the tool in an evaluation, and therefore not something to ask for *yet*
 * (Jira NAV-147)?
 *
 * The third reason a page has nothing to offer, and the only one that is
 * temporary. A refusal ends the errand and a passive tool needs no request;
 * this one says "come back". Asked apart from `isRefusal` for exactly the
 * reason that one is asked apart from `offersNoAction`: a reader who cannot
 * tell "we said no" from "we have not finished looking" learns the wrong thing
 * about their business unit.
 */
export function isUnderEvaluation(availability: ToolAvailability): boolean {
  return availability === "UNDER_REVIEW";
}

/**
 * Does this page have anything at all to offer for the tool, beyond saying
 * where the unit stands?
 *
 * Two availabilities leave it with nothing, for unrelated reasons: the business unit
 * refused the tool, or the business unit made it passively available to everybody and
 * there is nothing to ask for (ADR 0017, amended for Jira NAV-71). A single
 * predicate because the *page* treats them alike — no request, no
 * self-declaration, no take — while the panel above it must not, which is what
 * `isRefusal`'s separate one-line meaning is for.
 *
 * **UNDER_REVIEW is deliberately not one of them**, though it raises no request
 * either (Jira NAV-147). "I already have access" survives an evaluation for the
 * same reason it survives a withdrawal: a member telling the register something
 * true about themselves is a *record*, not a request, and an evaluation narrows
 * what can be asked for without narrowing what can be recorded. It is the state
 * where that record is worth most — a business unit deciding whether to buy a tool
 * wants to know who is already paying for it out of their own budget — so
 * dropping the button here would empty the register during exactly the window
 * the decision needs it. The rules agree and always did: they gate a TAKEN
 * grant on availability and permit a SELF_DECLARED one unconditionally.
 *
 * Derived from the route table rather than listed again, and that is the point
 * of writing it this way: a page offers a request exactly where one has
 * somewhere to go, and as two hand-written lists they were free to disagree.
 * The two words held out of it are the two with something else to offer —
 * READY, which is taken rather than asked for, and UNDER_REVIEW, which can
 * still be declared.
 */
export function offersNoAction(availability: ToolAvailability): boolean {
  return (
    requestRouteFor(availability) === null &&
    !isTakeable(availability) &&
    !isUnderEvaluation(availability)
  );
}

/**
 * May a member say they already have this one?
 *
 * Self-declaration (A5) is for a tool somebody obtained **outside Navigator**,
 * so the availabilities that contradict that are the ones excluded — and the
 * exclusion is a product decision rather than a tidy-up. ADR 0017 decision 1:
 * *"'I already have access' is not offered on a ready tool … offering it
 * invites a member to declare a thing that was never in question, and produces
 * two records of the same access with different meanings"*, which a
 * pre-installed tool already behaved that way for. `BLOCKED` and `PASSIVE` are
 * out because the page offers nothing at all for them.
 *
 * Here rather than on either surface that asks, because two of them now do:
 * the tool page reaches the same answers through the order of its render
 * branches, and the onboarding walkthrough has to reach them before it offers
 * somebody a list to tick (Jira NAV-26). One case is the page's alone and not
 * expressible here: a member whose automatic grant an admin revoked *is*
 * offered the control on a PREINSTALLED tool, because the fan-out will never
 * restore them — that turns on the person, not on the availability. A list built from what a business unit holds
 * *most* is otherwise a list of exactly the tools this rule excludes — a
 * pre-installed one is granted to every member of the unit, so it sits at the
 * top by construction.
 *
 * A `Record` for the reason `BUSINESS_UNIT_OFFERS` and `REQUEST_ROUTE` are: an eighth
 * availability has to fail to compile until somebody decides what it means
 * here. As a negative expression a new value became self-declarable silently,
 * and the walkthrough offers this answer *in bulk* on the first screen a new
 * colleague sees.
 */
const SELF_DECLARABLE: Record<ToolAvailability, boolean> = {
  // Already theirs for the taking, or granted by fan-out: declaring would make
  // a second record of an access that was never in question.
  READY: false,
  PREINSTALLED: false,
  QUICK: true,
  STANDARD: true,
  // No answer from the business unit yet, and somebody may hold it from before it was
  // ever asked about.
  UNDER_REVIEW: true,
  // The page offers nothing at all for these two.
  PASSIVE: false,
  BLOCKED: false,
};

export function isSelfDeclarable(availability: ToolAvailability): boolean {
  return SELF_DECLARABLE[availability];
}
